import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Phone, Mail, ExternalLink } from 'lucide-react';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Standard OSM tiles - always light, Carto tiles need an api key
const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

// Custom green golf marker icon - FSGU branded
const createGolfIcon = () => {
  return L.divIcon({
    className: 'custom-golf-marker',
    html: `
      <div style="
        width: 40px;
        height: 40px;
        background: linear-gradient(135deg, #66BB6A 0%, #43A047 100%);
        border: 3px solid #FFFFFF;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2), 0 0 0 4px rgba(102, 187, 106, 0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        <div style="
          transform: rotate(45deg);
          font-size: 20px;
          filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.3));
        ">⛳</div>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40]
  });
};

// Component to handle selected club centering
const CenterOnClub = ({ selectedClub }) => {
  const map = useMap();

  useEffect(() => {
    if (selectedClub) {
      map.flyTo([selectedClub.latitude, selectedClub.longitude], 12, {
        duration: 1.5
      });
    }
  }, [selectedClub, map]);

  return null;
};

const linkStyle = {
  fontSize: '14px',
  color: '#4CAF50',
  textDecoration: 'none',
  fontWeight: '500'
};

const iconStyle = { width: '16px', height: '16px', color: '#4CAF50' };

const GolfClubsMap = ({ clubs, selectedClub, onClubSelect, className }) => {
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef(null);

  // Center on Free State, South Africa
  const center = [-28.5, 26.5];
  const zoom = 7;

  const golfIcon = createGolfIcon();

  const handleMarkerClick = (club) => {
    if (onClubSelect) {
      onClubSelect(club);
    }
  };

  return (
    <div className={`relative ${className}`} style={{ minHeight: '500px' }}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ width: '100%', height: '100%', minHeight: '500px' }}
        scrollWheelZoom={true}
        ref={mapRef}
        whenReady={() => setMapReady(true)}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url={TILE_URL}
          maxZoom={19}
        />

        {/* Center on selected club */}
        {selectedClub && <CenterOnClub selectedClub={selectedClub} />}

        {/* Club markers */}
        {clubs.map((club) => (
          <Marker
            key={club.id}
            position={[club.latitude, club.longitude]}
            icon={golfIcon}
            eventHandlers={{
              click: () => handleMarkerClick(club),
            }}
          >
            <Popup className="golf-club-popup">
              <div className="p-3" style={{ minWidth: '280px', maxWidth: '320px' }}>
                <h3 className="font-bold text-lg mb-3 pr-4" style={{ color: '#1C1C1E', lineHeight: '1.4' }}>
                  {club.name}
                </h3>

                {club.address && (
                  <div className="flex items-start space-x-2 mb-3">
                    <MapPin className="flex-shrink-0 mt-0.5" style={iconStyle} />
                    <div style={{ fontSize: '14px', color: '#6D6D70', lineHeight: '1.5' }}>
                      <p className="font-medium" style={{ color: '#515154', marginBottom: '2px' }}>
                        {club.address}
                      </p>
                      {club.city && (
                        <p style={{ fontSize: '13px' }}>
                          {club.city}, {club.postal_code}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {club.phone && (
                  <div className="flex items-center space-x-2 mb-3">
                    <Phone className="flex-shrink-0" style={iconStyle} />
                    <a
                      href={`tel:${club.phone}`}
                      style={linkStyle}
                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                      {club.phone}
                    </a>
                  </div>
                )}

                {club.email && (
                  <div className="flex items-center space-x-2 mb-3">
                    <Mail className="flex-shrink-0" style={iconStyle} />
                    <a
                      href={`mailto:${club.email}`}
                      style={{ ...linkStyle, wordBreak: 'break-all' }}
                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                      {club.email}
                    </a>
                  </div>
                )}

                {club.website && (
                  <div className="flex items-center space-x-2 mb-3">
                    <ExternalLink className="flex-shrink-0" style={iconStyle} />
                    <a
                      href={club.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={linkStyle}
                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                      Visit Website
                    </a>
                  </div>
                )}

                {(club.holes || club.par) && (
                  <div className="mt-3 pt-3" style={{ borderTop: '1px solid #E5E5EA' }}>
                    <div className="flex items-center justify-between" style={{ fontSize: '13px', color: '#6D6D70' }}>
                      {club.holes && (
                        <span className="flex items-center">
                          <span style={{ marginRight: '4px' }}>⛳</span>
                          {club.holes} Holes
                        </span>
                      )}
                      {club.par && (
                        <span className="font-medium" style={{ color: '#515154' }}>
                          Par {club.par}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Map styling - light only */}
      <style>{`
        .leaflet-container {
          background: #F5F5F7;
          font-family: -apple-system, BlinkMacSystemFont, 'Avenir Next', 'Avenir', 'Segoe UI', sans-serif;
          border-radius: 1rem;
        }

        .leaflet-popup-content-wrapper {
          background: #FFFFFF;
          border-radius: 16px;
          padding: 0;
          box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.3);
          border: 1px solid #E5E5EA;
        }

        .leaflet-popup-content {
          margin: 16px;
          font-size: 14px;
          line-height: 1.6;
          color: #1C1C1E;
        }

        .leaflet-popup-tip {
          background: #FFFFFF;
          border: 1px solid #E5E5EA;
        }

        .leaflet-popup-close-button {
          font-size: 20px;
          padding: 4px 8px !important;
          color: #8E8E93 !important;
          font-weight: 300;
          top: 8px !important;
          right: 8px !important;
          width: 24px !important;
          height: 24px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          border-radius: 6px;
        }

        .leaflet-popup-close-button:hover {
          color: #4CAF50 !important;
          background: rgba(76, 175, 80, 0.1) !important;
        }

        .custom-golf-marker {
          background: transparent;
          border: none;
          transition: transform 0.2s ease;
        }

        .custom-golf-marker:hover {
          transform: scale(1.1);
        }

        .leaflet-control-zoom {
          border: none !important;
          border-radius: 12px !important;
          overflow: hidden;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }

        .leaflet-control-zoom a {
          background: #FFFFFF !important;
          color: #1C1C1E !important;
          border: none !important;
          width: 36px !important;
          height: 36px !important;
          line-height: 36px !important;
          font-size: 20px !important;
          transition: all 0.2s ease !important;
        }

        .leaflet-control-zoom a:hover {
          background: #66BB6A !important;
          color: #FFFFFF !important;
        }

        .leaflet-control-zoom a:first-child {
          border-radius: 12px 12px 0 0 !important;
        }

        .leaflet-control-zoom a:last-child {
          border-radius: 0 0 12px 12px !important;
        }

        .leaflet-control-attribution {
          background: rgba(255, 255, 255, 0.8) !important;
          color: #6D6D70 !important;
          font-size: 10px;
          padding: 4px 8px;
          border-radius: 8px 0 0 0;
          backdrop-filter: blur(10px);
        }

        .leaflet-control-attribution a {
          color: #4CAF50 !important;
        }

        .leaflet-control-scale {
          background: rgba(255, 255, 255, 0.8) !important;
          border: 1px solid #E5E5EA !important;
          border-radius: 8px;
          backdrop-filter: blur(10px);
        }

        .leaflet-tile-container {
          transition: opacity 0.3s ease;
        }

        @media (max-width: 768px) {
          .leaflet-popup-content-wrapper {
            max-width: 280px;
          }

          .leaflet-popup-content {
            margin: 12px;
            font-size: 13px;
          }

          .leaflet-control-zoom a {
            width: 32px !important;
            height: 32px !important;
            line-height: 32px !important;
            font-size: 18px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default GolfClubsMap;